import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import {
  listLineItemComments,
  addLineItemComment,
  deleteLineItemComment,
  type LineItemComment,
  type LineItemCommentListResponse,
} from '@/api/lineItemComments'
import { queryKeys } from '@/lib/queryKeys'
import type { DagTableLoadResponse } from '@/types/dag'

/**
 * Patch `comment_count` on any cached DAG row whose `line_ref_id` matches.
 * The badge in the grid reads from the dagTable rows cache, so it must be
 * updated in lockstep with optimistic add/delete on the thread cache.
 */
function patchDagRowCommentCount(
  queryClient: QueryClient,
  refId: string,
  delta: number
) {
  queryClient.setQueriesData<DagTableLoadResponse>(
    { queryKey: ['dag-table', 'rows'] },
    old => {
      if (!old?.rows) return old
      let changed = false
      const rows = old.rows.map(row => {
        if (row.line_ref_id !== refId) return row
        changed = true
        return {
          ...row,
          comment_count: Math.max(0, (row.comment_count ?? 0) + delta),
        }
      })
      if (!changed) return old
      return { ...old, rows }
    }
  )
}

export function useLineItemComments(
  refId: string | null,
  offset = 0,
  limit = 50
) {
  return useQuery<LineItemCommentListResponse>({
    queryKey: [
      ...queryKeys.lineItemComments.byRefId(refId ?? ''),
      offset,
      limit,
    ],
    queryFn: () => listLineItemComments(refId!, offset, limit),
    enabled: !!refId,
  })
}

export function useAddLineItemComment(refId: string) {
  const queryClient = useQueryClient()

  return useMutation<
    LineItemComment,
    Error,
    { content: string },
    { previous?: LineItemCommentListResponse; tempId: string }
  >({
    mutationFn: ({ content }) => addLineItemComment(refId, content),
    onMutate: async ({ content }) => {
      const prefix = queryKeys.lineItemComments.byRefId(refId)
      await queryClient.cancelQueries({ queryKey: prefix })

      const queries = queryClient.getQueriesData<LineItemCommentListResponse>({
        queryKey: prefix,
      })
      const previous = queries[0]?.[1]
      const tempId = `optimistic-${Date.now()}`
      const now = new Date().toISOString()

      const optimistic: LineItemComment = {
        id: tempId,
        content,
        created_at: now,
        updated_at: now,
        is_deleted: false,
        author: null,
      }

      queryClient.setQueriesData<LineItemCommentListResponse>(
        { queryKey: prefix },
        old => {
          if (!old) return old
          return {
            ...old,
            data: [optimistic, ...old.data],
            pagination: {
              ...old.pagination,
              total: old.pagination.total + 1,
            },
          }
        }
      )
      patchDagRowCommentCount(queryClient, refId, 1)

      return { previous, tempId }
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return
      const prefix = queryKeys.lineItemComments.byRefId(refId)
      queryClient.setQueriesData<LineItemCommentListResponse>(
        { queryKey: prefix },
        old => {
          if (!old) return old
          return {
            ...old,
            data: old.data.filter(c => c.id !== ctx.tempId),
            pagination: {
              ...old.pagination,
              total: Math.max(0, old.pagination.total - 1),
            },
          }
        }
      )
      patchDagRowCommentCount(queryClient, refId, -1)
    },
    onSuccess: (created, _vars, ctx) => {
      if (!ctx) return
      const prefix = queryKeys.lineItemComments.byRefId(refId)
      queryClient.setQueriesData<LineItemCommentListResponse>(
        { queryKey: prefix },
        old => {
          if (!old) return old
          return {
            ...old,
            data: old.data.map(c => (c.id === ctx.tempId ? created : c)),
          }
        }
      )
    },
  })
}

export function useDeleteLineItemComment(refId: string) {
  const queryClient = useQueryClient()

  return useMutation<
    void,
    Error,
    { commentId: string },
    { previous?: LineItemCommentListResponse }
  >({
    mutationFn: ({ commentId }) => deleteLineItemComment(commentId),
    onMutate: async ({ commentId }) => {
      const prefix = queryKeys.lineItemComments.byRefId(refId)
      await queryClient.cancelQueries({ queryKey: prefix })

      const queries = queryClient.getQueriesData<LineItemCommentListResponse>({
        queryKey: prefix,
      })
      const previous = queries[0]?.[1]

      queryClient.setQueriesData<LineItemCommentListResponse>(
        { queryKey: prefix },
        old => {
          if (!old) return old
          return {
            ...old,
            data: old.data.filter(c => c.id !== commentId),
            pagination: {
              ...old.pagination,
              total: Math.max(0, old.pagination.total - 1),
            },
          }
        }
      )
      patchDagRowCommentCount(queryClient, refId, -1)

      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx?.previous) return
      const prefix = queryKeys.lineItemComments.byRefId(refId)
      queryClient.setQueriesData<LineItemCommentListResponse>(
        { queryKey: prefix },
        ctx.previous
      )
      patchDagRowCommentCount(queryClient, refId, 1)
    },
  })
}
